/**
 * Pickles へ同意済みのページ閲覧を即時送信する。
 *
 * GA4 の _ga cookie と STRIGHT ONE の Consent Mode 設定を再利用し、氏名・メールアドレス・
 * クエリ文字列は送らない。Pickles 側ではフォーム承認後に既存の本人突合ゲートを通して表示する。
 */

const CONSENT_COOKIE = 'cookie_consent_gcm_data';
const CONSENT_DOMAIN = 'spice-factory.co.jp';
const SESSION_STORAGE_KEY = 'pickles_realtime_pageview_session_id';
const MAX_CONSENT_RETRIES = 12;
const CONSENT_RETRY_MS = 5000;

function readCookieValue(cookie, name) {
  const escapedName = name.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
  const match = cookie.match(new RegExp(`(?:^|;\\s*)${escapedName}=([^;]*)`));
  return match ? match[1] : null;
}

function parseConsent(value, domain) {
  if (!value) return null;
  try {
    const data = JSON.parse(decodeURIComponent(value));
    if (data?.cmd === domain && Array.isArray(data.gGcType))
      return data.gGcType;
    if (data?.[domain] && Array.isArray(data[domain].gGcType))
      return data[domain].gGcType;
  } catch {
    // 同意管理側の壊れた値は追跡を許可しない。
  }
  return null;
}

/** GA4 client_id を _ga Cookie の標準形式から安全に取り出す。 */
export function getGaClientId(cookie) {
  const raw = readCookieValue(cookie, '_ga');
  if (!raw) return null;
  try {
    const parts = decodeURIComponent(raw).split('.');
    const clientId = parts.length === 4 ? `${parts[2]}.${parts[3]}` : null;
    return clientId && clientId.length <= 64 && /^\d+\.\d+$/.test(clientId)
      ? clientId
      : null;
  } catch {
    return null;
  }
}

/** クエリ・fragment をネットワークへ出さないページURLに縮小する。 */
export function pageViewUrl(location) {
  return `${location.origin}${location.pathname}`;
}

/**
 * STRIGHT ONE の保存済み Consent Mode を読む。保存値がなければ GA4 と同じ既定に委ね、
 * _ga cookie が実際に存在する場合だけ送信する（getGaClientId がその最終ゲート）。これは
 * header.php の地域別 Consent Mode 既定と ADR 0051 の「拒否時は _ga を発行しない」前提に依存する。
 * 同意管理の既定を変更する場合は、ここも明示同意必須（false）へ変更する。
 */
export function hasAnalyticsConsent({
  cookie,
  storage,
  domain = CONSENT_DOMAIN,
}) {
  const cookieValue = readCookieValue(cookie, CONSENT_COOKIE);
  if (cookieValue) {
    const values = parseConsent(cookieValue, domain);
    return values ? values.includes('analytics_storage') : false;
  }

  try {
    const storageValue = storage?.getItem(CONSENT_COOKIE);
    if (storageValue) {
      const values = parseConsent(storageValue, domain);
      return values ? values.includes('analytics_storage') : false;
    }
  } catch {
    return false;
  }

  return true;
}

function getSessionId(storage) {
  try {
    const existing = storage.getItem(SESSION_STORAGE_KEY);
    if (existing) return existing;
    const sessionId =
      crypto.randomUUID?.() ||
      `${Date.now()}-${Math.random().toString(36).slice(2)}`;
    storage.setItem(SESSION_STORAGE_KEY, sessionId);
    return sessionId;
  } catch {
    return null;
  }
}

function sendPageView(config) {
  if (!hasAnalyticsConsent({ cookie: document.cookie, storage: localStorage }))
    return false;

  const clientId = getGaClientId(document.cookie);
  if (!clientId) return false;

  const sessionId = getSessionId(sessionStorage);
  const payload = {
    clientId,
    pageUrl: pageViewUrl(window.location),
    ...(sessionId ? { sessionId } : {}),
  };
  void fetch(config.endpoint, {
    method: 'POST',
    mode: 'cors',
    credentials: 'omit',
    keepalive: true,
    headers: { 'content-type': 'application/json' },
    body: JSON.stringify(payload),
  }).catch(() => undefined);
  return true;
}

function initializeRealtimePageView() {
  const config = window.spicePicklesRealtimePageView;
  if (!config?.endpoint) return;

  if (sendPageView(config)) return;

  // 初回表示後に同意した場合も、ページ遷移を待たずに一度だけ送信を試みる。
  let attempts = 0;
  const timer = window.setInterval(() => {
    attempts += 1;
    if (sendPageView(config) || attempts >= MAX_CONSENT_RETRIES)
      window.clearInterval(timer);
  }, CONSENT_RETRY_MS);
}

if (typeof window !== 'undefined') initializeRealtimePageView();
